import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowRight, Calendar as CalendarIcon, Plus, Trash2, Check, ChevronLeft, ChevronRight, BookOpen, StickyNote, ArrowRightLeft, Clock, CopyPlus } from 'lucide-react';
import { createDailyTask, getDailyTasks, updateDailyTask, toggleTaskCompletion, deleteDailyTask, syncDailyTaskToCalendar, syncAllDailyTasksToCalendar, getCustomers, getGoogleCalendarStatus, syncTasksToGoogleCalendar, getGoogleCalendarAuthUrl } from '../api';
import { toast } from '../hooks/use-toast';
import { matchesCustomer } from '../lib/customerSearch';

const DailyJournal = () => {
  const navigate = useNavigate();
  // Get today's date in local timezone
  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const [selectedDate, setSelectedDate] = useState(todayString);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [taskToMove, setTaskToMove] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [isMoveAllDialogOpen, setIsMoveAllDialogOpen] = useState(false);
  const [moveAllDate, setMoveAllDate] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedHour, setSelectedHour] = useState('09');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [gcalConnected, setGcalConnected] = useState(false);
  const [gcalSyncing, setGcalSyncing] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    time: '',
    equipmentList: ''
  });

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  useEffect(() => {
    loadAllTasks();
    loadCustomers();
    checkGcalStatus();
  }, []);

  const checkGcalStatus = async () => {
    try {
      const status = await getGoogleCalendarStatus();
      setGcalConnected(status.connected && !status.needsReconnect);
      if (status.needsReconnect) {
        toast({ title: 'החיבור ל-Google Calendar פג', description: 'התזכורות לטלפון לא נשלחות. לחץ "חבר Google Calendar" כדי להתחבר מחדש', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Error checking Google Calendar status:', e);
    }
  };

  const handleGcalSync = async () => {
    if (!gcalConnected) {
      try {
        const { authorization_url } = await getGoogleCalendarAuthUrl();
        window.location.href = authorization_url;
      } catch (e) {
        toast({ title: 'שגיאה', description: 'לא ניתן להתחבר ל-Google Calendar', variant: 'destructive' });
      }
      return;
    }
    setGcalSyncing(true);
    try {
      const result = await syncTasksToGoogleCalendar(selectedDate);
      if (result.failed > 0) {
        toast({ title: 'סנכרון חלקי', description: `${result.synced} סונכרנו, ${result.failed} נכשלו: ${result.error || ''}`, variant: 'destructive' });
      } else if (result.synced > 0) {
        toast({ title: 'סונכרן!', description: `${result.synced} משימות סונכרנו ל-Google Calendar (תזכורת שעה לפני)` });
      } else {
        toast({ title: 'אין משימות', description: 'אין משימות לסנכרון ביום זה' });
      }
    } catch (e) {
      if (e.response?.status === 401) setGcalConnected(false);
      toast({ title: 'שגיאה', description: e.response?.data?.detail || 'לא ניתן לסנכרן ל-Google Calendar', variant: 'destructive' });
    } finally {
      setGcalSyncing(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setAllCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  useEffect(() => {
    if (allTasks.length > 0 || selectedDate) {
      loadTasksForDate(selectedDate);
    }
  }, [selectedDate, allTasks]);

  const loadAllTasks = async () => {
    try {
      const data = await getDailyTasks();
      console.log('Loaded tasks from server:', data.length, 'tasks');
      setAllTasks(data);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const loadTasksForDate = (date) => {
    const filtered = allTasks.filter(task => task.date === date);
    console.log(`Filtered tasks for ${date}:`, filtered.length, 'tasks');
    setTasks(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא שם לקוח',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      
      const taskData = {
        ...formData,
        date: selectedDate
      };

      console.log('Creating task:', taskData);
      const result = await createDailyTask(taskData);
      console.log('Task created successfully:', result);
      
      // Add task directly to state instead of reloading
      setAllTasks(prevTasks => [...prevTasks, result]);
      
      // If the task is for selected date, add it to current tasks too
      if (result.date === selectedDate) {
        setTasks(prevTasks => [...prevTasks, result]);
      }
      
      toast({
        title: 'הצלחה',
        description: 'המשימה נוספה בהצלחה'
      });

      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('❌ Error creating task:', error);
      toast({
        title: 'שגיאה',
        description: `לא ניתן ליצור משימה: ${error.message}`,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplete = async (taskId) => {
    try {
      await toggleTaskCompletion(taskId);
      await loadAllTasks();
    } catch (error) {
      console.error('Error toggling task:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לעדכן משימה',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק משימה זו?')) {
      return;
    }

    try {
      await deleteDailyTask(taskId);
      toast({
        title: 'הצלחה',
        description: 'המשימה נמחקה'
      });
      await loadAllTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן למחוק משימה',
        variant: 'destructive'
      });
    }
  };

  const handleMoveTask = (task) => {
    setTaskToMove(task);
    setNewDate(task.date);
    setIsMoveDialogOpen(true);
  };

  const handleConfirmMove = async () => {
    if (!taskToMove || !newDate) return;

    try {
      const updatedTask = {
        ...taskToMove,
        date: newDate
      };
      delete updatedTask.id;
      delete updatedTask.createdDate;

      await updateDailyTask(taskToMove.id, updatedTask);
      
      toast({
        title: 'הצלחה',
        description: 'המשימה הועברה בהצלחה'
      });

      setIsMoveDialogOpen(false);
      setTaskToMove(null);
      await loadAllTasks();
    } catch (error) {
      console.error('Error moving task:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן להעביר משימה',
        variant: 'destructive'
      });
    }
  };

  const handleOpenNote = (task) => {
    setCurrentTask(task);
    setNoteText(task.note || '');
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!currentTask) return;

    try {
      const updatedTask = {
        ...currentTask,
        note: noteText
      };
      delete updatedTask.id;
      delete updatedTask.createdDate;

      await updateDailyTask(currentTask.id, updatedTask);
      
      toast({
        title: 'הצלחה',
        description: 'ההערה נשמרה'
      });

      setNoteDialogOpen(false);
      setCurrentTask(null);
      await loadAllTasks();
    } catch (error) {
      console.error('Error saving note:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לשמור הערה',
        variant: 'destructive'
      });
    }
  };

  const handleOpenMoveAll = () => {
    // Default to next day after selected date
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    const nextDay = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setMoveAllDate(nextDay);
    setIsMoveAllDialogOpen(true);
  };

  const handleConfirmMoveAll = async () => {
    if (!moveAllDate) return;
    const tasksToMove = tasks.filter(t => !t.completed);
    if (tasksToMove.length === 0) {
      toast({ title: 'אין משימות להעברה', description: 'אין משימות פעילות ליום זה' });
      setIsMoveAllDialogOpen(false);
      return;
    }

    try {
      setLoading(true);
      let moved = 0;
      for (const task of tasksToMove) {
        const updatedTask = { ...task, date: moveAllDate };
        delete updatedTask.id;
        delete updatedTask.createdDate;
        await updateDailyTask(task.id, updatedTask);
        moved++;
      }
      toast({
        title: 'הצלחה',
        description: `${moved} משימות הועברו ל-${new Date(moveAllDate).toLocaleDateString('he-IL')}`
      });
      setIsMoveAllDialogOpen(false);
      await loadAllTasks();
    } catch (error) {
      console.error('Error moving all tasks:', error);
      toast({ title: 'שגיאה', description: 'לא ניתן להעביר את המשימות', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      time: '',
      equipmentList: ''
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const getTasksForDate = (date) => {
    return allTasks.filter(task => task.date === date);
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const days = [];
    const dayNames = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      // Create date string directly to avoid timezone issues
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isSelected = dateString === selectedDate;
      const today = new Date();
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const isToday = dateString === todayString;
      const dayTasks = getTasksForDate(dateString);
      const hasCompleted = dayTasks.some(t => t.completed);
      const hasIncomplete = dayTasks.some(t => !t.completed);
      
      days.push(
        <div
          key={day}
          onClick={() => setSelectedDate(dateString)}
          className={`
            p-2 text-center cursor-pointer rounded-lg transition-all relative
            ${isSelected ? 'bg-blue-600 text-white font-bold' : 'hover:bg-blue-100'}
            ${isToday && !isSelected ? 'border-2 border-blue-600' : ''}
          `}
        >
          <div className="text-lg">{day}</div>
          {dayTasks.length > 0 && (
            <div className="flex justify-center gap-1 mt-1">
              {hasCompleted && <div className="w-2 h-2 bg-green-500 rounded-full"></div>}
              {hasIncomplete && <div className="w-2 h-2 bg-orange-500 rounded-full"></div>}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div>
        <div className="grid grid-cols-7 gap-2 mb-2">
          {dayNames.map((name, i) => (
            <div key={i} className="text-center font-bold text-gray-600 p-2">
              {name}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {days}
        </div>
      </div>
    );
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <Card className="shadow-2xl border-t-4 border-blue-500">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <CardTitle className="text-3xl font-bold flex items-center">
                  <BookOpen className="ml-3 w-8 h-8" />
                  יומן יומי
                </CardTitle>
                <Button
                  variant="outline"
                  disabled={isSyncing}
                  onClick={async () => {
                    if (isSyncing) return;
                    setIsSyncing(true);
                    try {
                      const result = await syncAllDailyTasksToCalendar();
                      if (result.synced_count === 0 && result.failed_count === 0) {
                        toast({
                          title: 'אין משימות חדשות',
                          description: 'כל המשימות כבר סונכרנו ל-Outlook Calendar',
                        });
                      } else if (result.synced_count > 0) {
                        toast({
                          title: 'הצלחה!',
                          description: `${result.synced_count} משימות סונכרנו ל-Outlook Calendar`,
                        });
                        await loadAllTasks();
                      } else {
                        toast({
                          title: 'שגיאה',
                          description: 'לא ניתן לסנכרן משימות',
                          variant: 'destructive'
                        });
                      }
                    } catch (error) {
                      toast({
                        title: 'שגיאה',
                        description: 'לא ניתן לסנכרן ל-Outlook Calendar',
                        variant: 'destructive'
                      });
                    } finally {
                      setIsSyncing(false);
                    }
                  }}
                  className="border-white text-white hover:bg-white/20 bg-indigo-700 disabled:opacity-50"
                  data-testid="sync-all-outlook-button"
                >
                  {isSyncing ? 'מסנכרן...' : 'סנכרן הכל ל-Outlook'}
                </Button>
                <Button
                  variant="outline"
                  disabled={gcalSyncing}
                  onClick={handleGcalSync}
                  className="border-white text-white hover:bg-white/20 bg-green-600 disabled:opacity-50"
                  data-testid="sync-google-calendar-button"
                >
                  <svg className="w-4 h-4 ml-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  {gcalSyncing ? 'מסנכרן...' : gcalConnected ? `סנכרן ל-Google Calendar` : 'חבר Google Calendar'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleOpenMoveAll}
                  className="border-white text-white hover:bg-white/20 bg-orange-600"
                  data-testid="move-all-tasks-button"
                >
                  <CopyPlus className="w-4 h-4 ml-1" />
                  העבר הכל ליום אחר
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (tasks.length === 0) {
                      toast({ title: 'אין משימות', description: 'אין משימות לשליחה ביום זה' });
                      return;
                    }
                    const dateDisplay = new Date(selectedDate + 'T00:00:00').toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                    let msg = `📋 *משימות ליום ${dateDisplay}*\n\n`;
                    tasks.forEach((t, i) => {
                      const status = t.completed ? '✅' : '⬜';
                      msg += `${status} ${i + 1}. *${t.title}*`;
                      if (t.time) msg += ` (${t.time})`;
                      msg += '\n';
                      if (t.description) msg += `   ${t.description}\n`;
                      if (t.equipmentList) msg += `   🔧 ${t.equipmentList}\n`;
                      if (t.note) msg += `   📝 ${t.note}\n`;
                    });
                    msg += `\n📊 סה"כ: ${tasks.length} משימות | ${tasks.filter(t => t.completed).length} הושלמו`;
                    if (navigator.share) {
                      navigator.share({ text: msg }).catch(() => {});
                    } else {
                      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, '_blank');
                    }
                  }}
                  className="border-white text-white hover:bg-white/20 bg-emerald-700"
                  data-testid="share-whatsapp-button"
                >
                  <svg className="w-4 h-4 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  שלח / שתף משימות
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-8">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Calendar */}
              <div className="md:col-span-2">
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
                  <div className="flex justify-between items-center mb-6">
                    <Button variant="outline" onClick={previousMonth}>
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                    <h3 className="text-2xl font-bold text-gray-800">
                      {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </h3>
                    <Button variant="outline" onClick={nextMonth}>
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                  </div>
                  {renderCalendar()}
                  <div className="mt-4 flex gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span>משימות שהושלמו</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span>משימות פעילות</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tasks for selected date */}
              <div>
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200 mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">
                      {new Date(selectedDate).toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </h3>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4 ml-1" />
                          הוסף משימה
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg" dir="rtl">
                        <DialogHeader>
                          <DialogTitle className="text-2xl font-bold">משימה חדשה</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                          <div className="space-y-2 relative">
                            <Label htmlFor="title">שם לקוח <span className="text-red-500">*</span></Label>
                            <Input
                              id="title"
                              value={formData.title}
                              onChange={(e) => {
                                setFormData({ ...formData, title: e.target.value });
                                const val = e.target.value.trim();
                                if (val.length > 0) {
                                  const matches = allCustomers.filter(c => matchesCustomer(c, val));
                                  setCustomerSuggestions(matches);
                                  setShowCustomerSuggestions(matches.length > 0);
                                } else {
                                  setShowCustomerSuggestions(false);
                                }
                              }}
                              onFocus={() => {
                                const val = (formData.title || '').trim();
                                if (val.length > 0) {
                                  const matches = allCustomers.filter(c => matchesCustomer(c, val));
                                  if (matches.length > 0) setShowCustomerSuggestions(true);
                                }
                              }}
                              onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                              placeholder="הזן שם או מספר לקוח"
                              required
                              autoComplete="off"
                              data-testid="task-customer-name"
                            />
                            {showCustomerSuggestions && customerSuggestions.length > 0 && (
                              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-indigo-300 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                {customerSuggestions.map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    className="w-full text-right px-4 py-3 hover:bg-indigo-50 transition-colors flex items-center justify-between border-b border-gray-100 last:border-0"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setFormData({ ...formData, title: `${c.firstName} ${c.lastName}`.trim() });
                                      setShowCustomerSuggestions(false);
                                    }}
                                  >
                                    <span className="text-sm font-medium text-gray-800"><span className="font-mono font-bold text-gray-500 ml-2">#{c.customerNumber}</span>{c.firstName} {c.lastName}</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.customerType === 'business' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                      {c.customerType === 'business' ? 'עסקי' : 'פרטי'}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="description">תיאור</Label>
                            <Textarea
                              id="description"
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              placeholder="הזן תיאור (אופציונלי)"
                              rows={3}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>שעה</Label>
                            <div className="relative">
                              <div 
                                onClick={() => setShowTimePicker(!showTimePicker)}
                                className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-400 transition-all bg-white"
                              >
                                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full shadow-lg">
                                  <Clock className="w-5 h-5" />
                                </div>
                                <span className="text-2xl font-bold text-gray-700">
                                  {formData.time || '--:--'}
                                </span>
                              </div>
                              
                              {showTimePicker && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-2xl p-4 z-50">
                                  <div className="flex gap-4 justify-center items-center">
                                    {/* Hours */}
                                    <div className="text-center">
                                      <div className="text-sm text-gray-500 mb-2">שעה</div>
                                      <div className="grid grid-cols-6 gap-1 max-h-32 overflow-y-auto">
                                        {hours.map(hour => (
                                          <button
                                            key={hour}
                                            type="button"
                                            onClick={() => {
                                              setSelectedHour(hour);
                                              setFormData({ ...formData, time: `${hour}:${selectedMinute}` });
                                            }}
                                            className={`w-10 h-10 rounded-lg font-bold transition-all ${
                                              selectedHour === hour 
                                                ? 'bg-blue-600 text-white' 
                                                : 'bg-gray-100 hover:bg-blue-100 text-gray-700'
                                            }`}
                                          >
                                            {hour}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    <div className="text-3xl font-bold text-gray-400">:</div>
                                    
                                    {/* Minutes */}
                                    <div className="text-center">
                                      <div className="text-sm text-gray-500 mb-2">דקות</div>
                                      <div className="grid grid-cols-2 gap-2">
                                        {minutes.map(minute => (
                                          <button
                                            key={minute}
                                            type="button"
                                            onClick={() => {
                                              setSelectedMinute(minute);
                                              setFormData({ ...formData, time: `${selectedHour}:${minute}` });
                                            }}
                                            className={`w-14 h-12 rounded-lg font-bold text-lg transition-all ${
                                              selectedMinute === minute 
                                                ? 'bg-indigo-600 text-white' 
                                                : 'bg-gray-100 hover:bg-indigo-100 text-gray-700'
                                            }`}
                                          >
                                            {minute}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex gap-2 mt-4 justify-center">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setFormData({ ...formData, time: '' });
                                        setShowTimePicker(false);
                                      }}
                                    >
                                      נקה
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="bg-blue-600 hover:bg-blue-700"
                                      onClick={() => setShowTimePicker(false)}
                                    >
                                      אישור
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="equipmentList">רשימת ציוד</Label>
                            <Textarea
                              id="equipmentList"
                              value={formData.equipmentList}
                              onChange={(e) => setFormData({ ...formData, equipmentList: e.target.value })}
                              placeholder="הזן רשימת ציוד (אופציונלי)"
                              rows={3}
                            />
                          </div>
                          <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                              ביטול
                            </Button>
                            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                              {loading ? 'שומר...' : 'הוסף משימה'}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      אין משימות ליום זה
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {tasks.map((task) => (
                        <div
                          key={task.id}
                          className={`border-2 rounded-lg p-4 transition-all ${
                            task.completed ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={task.completed}
                              onCheckedChange={() => handleToggleComplete(task.id)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className={`font-semibold ${task.completed ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                                  {task.title}
                                </h4>
                                {task.time && (
                                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full shadow-lg">
                                    <span className="text-sm font-bold">{task.time}</span>
                                  </div>
                                )}
                              </div>
                              {task.description && (
                                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                              )}
                              {task.equipmentList && (
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                  <strong className="text-yellow-800">רשימת ציוד:</strong>
                                  <p className="text-gray-700 mt-1 whitespace-pre-wrap">{task.equipmentList}</p>
                                </div>
                              )}
                              {task.note && (
                                <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs">
                                  <strong className="text-purple-800">הערה:</strong>
                                  <p className="text-gray-700 mt-1 whitespace-pre-wrap">{task.note}</p>
                                </div>
                              )}
                              {task.microsoft_calendar_event_id && (
                                <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  <Check className="w-3 h-3" />
                                  סונכרן ל-Outlook
                                </div>
                              )}
                              <div className="flex gap-2 mt-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenNote(task)}
                                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-300 text-xs"
                                >
                                  <StickyNote className="w-3 h-3 ml-1" />
                                  {task.note ? 'ערוך הערה' : 'הוסף הערה'}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMoveTask(task)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300 text-xs"
                                >
                                  <ArrowRightLeft className="w-3 h-3 ml-1" />
                                  העבר לתאריך אחר
                                </Button>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(task.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Statistics */}
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl shadow-lg p-6 text-white">
                  <h4 className="text-lg font-bold mb-3">סטטיסטיקה</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>סה&quot;כ משימות היום:</span>
                      <span className="font-bold">{tasks.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>הושלמו:</span>
                      <span className="font-bold">{tasks.filter(t => t.completed).length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>ממתינות:</span>
                      <span className="font-bold">{tasks.filter(t => !t.completed).length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Note Dialog */}
        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="max-w-lg" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">הערה למשימה</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="noteText">הערה</Label>
                <Textarea
                  id="noteText"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="הזן הערה למשימה"
                  rows={5}
                  className="resize-none"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setNoteDialogOpen(false)}>
                  ביטול
                </Button>
                <Button onClick={handleSaveNote} className="bg-purple-600 hover:bg-purple-700">
                  שמור הערה
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Move Task Dialog */}
        <Dialog open={isMoveDialogOpen} onOpenChange={setIsMoveDialogOpen}>
          <DialogContent className="max-w-lg" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">העבר משימה לתאריך אחר</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="newDate">תאריך חדש</Label>
                <Input
                  id="newDate"
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsMoveDialogOpen(false)}>
                  ביטול
                </Button>
                <Button onClick={handleConfirmMove} className="bg-blue-600 hover:bg-blue-700">
                  העבר משימה
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Move All Tasks Dialog */}
        <Dialog open={isMoveAllDialogOpen} onOpenChange={setIsMoveAllDialogOpen}>
          <DialogContent className="max-w-lg" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">העברת כל המשימות ליום אחר</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-gray-600">
                {tasks.filter(t => !t.completed).length} משימות פעילות יועברו מ-{new Date(selectedDate).toLocaleDateString('he-IL')}
              </p>
              <div className="space-y-2">
                <Label htmlFor="moveAllDate">תאריך יעד</Label>
                <Input
                  id="moveAllDate"
                  type="date"
                  value={moveAllDate}
                  onChange={(e) => setMoveAllDate(e.target.value)}
                  className="w-full"
                  data-testid="move-all-date-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsMoveAllDialogOpen(false)}>
                  ביטול
                </Button>
                <Button onClick={handleConfirmMoveAll} disabled={loading} className="bg-orange-600 hover:bg-orange-700" data-testid="move-all-confirm-button">
                  {loading ? 'מעביר...' : 'העבר הכל'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DailyJournal;
